import { Outlet } from 'react-router-dom'

import { useI18n } from '../lib/i18n.jsx'
import { Button, ErrorState, Skeleton } from '../ui/index.js'
import SiteFooter from './SiteFooter.jsx'
import SiteHeader from './SiteHeader.jsx'
import { useSite } from './useSiteData.js'

/**
 * Shell for every public page.
 *
 * Three responsibilities and nothing else: put the header above the content,
 * the footer below it, and give a keyboard visitor a way past the navigation.
 * The skip link is the first focusable element on the page and jumps to
 * `#main` — without it, reaching the first paragraph of a news article costs
 * seven Tab presses on every single page.
 *
 * `<main>` carries the landmark role and the scroll target; pages render their
 * own `Section`/`Container` inside it rather than fighting the shell for width.
 *
 * 所有公开页的外壳。
 *
 * 只做三件事：把页头放在内容之上、页脚放在其下，并给键盘用户一条绕过导航的路。
 * 跳转链接是页面第一个可聚焦元素，直达 #main——没有它，每一页读到正文第一段都要按
 * 七次 Tab。
 *
 * <main> 承担地标角色和滚动锚点；页面在其内部自行渲染 Section / Container，
 * 而不是和外壳争夺宽度。
 */

/**
 * Public shell: skip link, header, routed content, footer.
 *
 * 公开外壳：跳转链接、页头、路由内容、页脚。
 *
 * @returns {JSX.Element} Layout / 页面外壳。
 */
export default function PublicLayout() {
  const { t } = useI18n()
  const { data: site } = useSite()

  // A number out of the schema's fixed option set, never free text — a stray
  // value here would multiply every size on the site by whatever was typed.
  // 取自 schema 固定选项集的数字，绝不接受自由输入——这里混进一个野值，
  // 会把全站每一个字号都乘上它。
  const scale = Number(site?.typeScale)
  const typeScaleStyle =
    Number.isFinite(scale) && scale > 0 ? { '--type-scale': String(scale) } : undefined

  return (
    // The admin-only type-scale setting is applied here, on the public shell,
    // rather than on <html>: the console keeps its own designed density no
    // matter what the public site is set to. A variable on a wrapper cascades to
    // its subtree and nowhere else.
    // 仅管理员可改的字号档位写在这里、也就是公开页外壳上，而不是写在 <html> 上：
    // 无论公开站点设成多大，控制台保持它自己设计好的信息密度。
    // 挂在包裹元素上的变量只向它的子树级联，不外溢。
    <div className="site-shell" style={typeScaleStyle}>
      <a className="site-skiplink" href="#main">
        {t('nav.skipToContent')}
      </a>
      <SiteHeader />
      <main className="site-main" id="main" tabIndex={-1}>
        <Outlet />
      </main>
      <SiteFooter />
    </div>
  )
}

/**
 * Loading / error / empty gate around a page's body.
 *
 * Every page owes a visitor the same three honest answers — "still fetching",
 * "the request failed, here is a retry", "there is genuinely nothing here yet" —
 * and writing that trio ten times invites ten slightly different behaviours. It
 * lives in the shell file because it is page *chrome*, not domain content: the
 * pages own what to show, this owns when to show it.
 *
 * The empty state is passed in rather than derived, because honest empty copy
 * is specific ("no publications yet" reads nothing like "no news yet") and the
 * design spec treats it as real content, not a fallback.
 *
 * 页面主体外层的「加载 / 出错 / 空」闸门。
 *
 * 每个页面都欠访客同样三个诚实的回答——「还在取」「请求失败了，这是重试」
 * 「这里确实还没有内容」——把这三段写十遍，就会得到十种略有差异的行为。它放在外壳
 * 文件里，是因为它属于页面「框架」而不是领域内容：显示什么由页面决定，何时显示
 * 由它决定。
 *
 * 空状态由调用方传入而不是在这里推导，因为诚实的空状态文案是具体的（「还没有论文」
 * 和「还没有新闻」读起来完全不同），而设计规范把它当作真正的内容而非兜底。
 *
 * @param {object} props Props / 属性。
 * @param {boolean} props.loading Request in flight / 请求进行中。
 * @param {Error|null} [props.error] Failure to report / 需要展示的失败。
 * @param {() => void} [props.onRetry] Retry handler, usually a hook's `reload` / 重试回调，通常是 hook 的 reload。
 * @param {boolean} [props.isEmpty] True when the payload arrived but holds nothing / 数据已到但内容为空时为 true。
 * @param {React.ReactNode} [props.empty] Empty state to render / 空状态内容。
 * @param {number} [props.lines] Skeleton line count while loading / 加载时骨架屏行数。
 * @returns {JSX.Element|null} One of the four states / 四种状态之一。
 */
export function PageState({ loading, error, onRetry, isEmpty = false, empty = null, lines = 5, children }) {
  const { t } = useI18n()

  if (loading) {
    return (
      <div className="page-state page-state--loading">
        <Skeleton lines={lines} />
      </div>
    )
  }

  if (error) {
    // A dead backend and a rejected request need different advice, and the API
    // layer already distinguishes them with `status === 0`.
    // 后端没起来和请求被拒需要给出不同的建议，而 API 层已用 status === 0 区分了两者。
    const offline = error.status === 0
    return (
      <ErrorState
        className="page-state page-state--error"
        title={t(offline ? 'error.network' : 'error.generic')}
        body={offline ? t('error.networkBody') : error.message}
        action={
          onRetry && (
            <Button variant="ghost" onClick={onRetry}>
              {t('common.retry')}
            </Button>
          )
        }
      />
    )
  }

  if (isEmpty) return empty

  return <>{children}</>
}
